import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { camerasAPI } from '../utils/api';
import { useSiteStore } from '../stores/siteStore';
import { useLiveStreamStore } from '../stores/liveStreamStore';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { formatDateTime } from '../utils/formatDateTime';
import {
  VideoCameraIcon,
  SignalIcon,
  SignalSlashIcon,
  EyeIcon,
  Cog6ToothIcon,
  ArrowPathIcon,
  XMarkIcon,
  PlusIcon,
  PlayIcon,
  StopIcon,
  CameraIcon,
  ExclamationTriangleIcon,
  CheckIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
  MagnifyingGlassIcon,
  WifiIcon,
  PencilSquareIcon,
  TrashIcon,
  CpuChipIcon,
} from '@heroicons/react/24/outline';
import ConfirmationModal from '../components/ConfirmationModal';
import DetectionPanel from '../components/cameras/DetectionPanel';
import DetectionEventFeed from '../components/cameras/DetectionEventFeed';
import DiscoverCamerasModal from '../components/cameras/DiscoverCamerasModal';

export default function CamerasPage() {
  const { selectedSite } = useSiteStore();
  const [viewMode, setViewMode] = useState('grid');
  const [gridLayout, setGridLayout] = useState('auto'); // 'auto', '2x2', '3x3'
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDiscoverModal, setShowDiscoverModal] = useState(false);
  const [editingCamera, setEditingCamera] = useState(null);
  const [showInactiveCameras, setShowInactiveCameras] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['cameras', selectedSite?.id, showInactiveCameras],
    queryFn: () => camerasAPI.getCameras(selectedSite?.id, { includeInactive: showInactiveCameras ? 'true' : 'false' }),
    refetchInterval: 30000,
  });

  const allCameras = data?.data?.data || [];

  // Filter cameras: show only active by default, or all if toggle is checked
  const cameras = showInactiveCameras ? allCameras : allCameras.filter(c => c.isActive);

  // Mutations for soft delete and delete
  const softDeleteMutation = useMutation({
    mutationFn: (cameraId) => camerasAPI.updateCamera(cameraId, { isActive: false }),
    onSuccess: () => {
      toast.success('Camera deactivated successfully');
      // Clear all camera-related queries from cache
      queryClient.removeQueries({ queryKey: ['cameras'] });
      // Explicitly refetch with current parameters
      refetch();
      setSelectedCamera(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to deactivate camera');
    },
  });

  const restoreMutation = useMutation({
    mutationFn: (cameraId) => camerasAPI.updateCamera(cameraId, { isActive: true }),
    onSuccess: () => {
      toast.success('Camera restored successfully');
      queryClient.removeQueries({ queryKey: ['cameras'] });
      refetch();
      setSelectedCamera(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to restore camera');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (cameraId) => camerasAPI.deleteCamera(cameraId),
    onSuccess: () => {
      toast.success('Camera deleted permanently');
      queryClient.removeQueries({ queryKey: ['cameras'] });
      refetch();
      setSelectedCamera(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to delete camera');
    },
  });

  // Status counts should only include active cameras
  const activeCameras = allCameras.filter(c => c.isActive);
  const statusCounts = {
    online: activeCameras.filter(c => c.status === 'online').length,
    offline: activeCameras.filter(c => c.status === 'offline').length,
    error: activeCameras.filter(c => c.status === 'error').length,
  };

  const handleCameraAdded = () => {
    setShowAddModal(false);
    queryClient.invalidateQueries({ queryKey: ['cameras'] });
    refetch();
  };

  const handleSoftDelete = (camera) => {
    setDeleteTarget({
      camera,
      type: 'soft',
      title: 'Deactivate Camera',
      message: `Are you sure you want to deactivate "${camera.name}"? It can be restored later.`,
      confirmLabel: 'Deactivate',
      variant: 'warning',
    });
  };

  const handleHardDelete = (camera) => {
    setDeleteTarget({
      camera,
      type: 'hard',
      title: 'Delete Camera Permanently',
      message: `Are you sure you want to permanently delete "${camera.name}"? This action cannot be undone.`,
      confirmLabel: 'Delete Permanently',
      variant: 'danger',
    });
  };

  const handleRestore = (camera) => {
    setDeleteTarget({
      camera,
      type: 'restore',
      title: 'Restore Camera',
      message: `Restore "${camera.name}" to active status?`,
      confirmLabel: 'Restore',
      variant: 'info',
    });
  };

  const handleConfirmDelete = () => {
    if (!deleteTarget) return;

    switch (deleteTarget.type) {
      case 'soft':
        softDeleteMutation.mutate(deleteTarget.camera.id);
        break;
      case 'hard':
        deleteMutation.mutate(deleteTarget.camera.id);
        break;
      case 'restore':
        restoreMutation.mutate(deleteTarget.camera.id);
        break;
      default:
        break;
    }
    setDeleteTarget(null);
  };

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Camera Management</h1>
          <p className="page-description">Monitor and configure safety cameras</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => refetch()}
            className="icon-btn"
            title="Refresh"
          >
            <ArrowPathIcon className="w-5 h-5" />
          </button>
          <button
            onClick={() => setShowDiscoverModal(true)}
            className="btn btn-secondary flex items-center gap-2"
            title="Discover ONVIF cameras on network"
          >
            <WifiIcon className="w-5 h-5" />
            <span className="hidden sm:inline">Discover</span>
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="btn btn-primary flex items-center gap-2"
          >
            <PlusIcon className="w-5 h-5" />
            <span className="hidden sm:inline">Add Camera</span>
          </button>
        </div>
      </div>

      {/* Status Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatusCard
          title="Online"
          count={statusCounts.online}
          total={cameras.length}
          color="green"
          icon={SignalIcon}
          index={0}
        />
        <StatusCard
          title="Offline"
          count={statusCounts.offline}
          total={cameras.length}
          color="gray"
          icon={SignalSlashIcon}
          index={1}
        />
        <StatusCard
          title="Error"
          count={statusCounts.error}
          total={cameras.length}
          color="red"
          icon={ExclamationTriangleIcon}
          index={2}
        />
      </div>

      {/* Show Inactive Cameras Toggle */}
      {!isLoading && allCameras.some(c => !c.isActive) && (
        <div className="glass-card p-4 flex items-center justify-between">
          <label className="flex items-center gap-3 cursor-pointer group">
            <input
              type="checkbox"
              checked={showInactiveCameras}
              onChange={(e) => setShowInactiveCameras(e.target.checked)}
              className="w-4 h-4 rounded border-slate-300 text-blue-600"
            />
            <span className="text-sm text-slate-600 group-hover:text-slate-800">
              Show inactive cameras ({allCameras.filter(c => !c.isActive).length})
            </span>
          </label>
        </div>
      )}

      {/* View Toggle */}
      <div className="flex items-center gap-3 animate-fade-in" style={{ animationDelay: '0.2s' }}>
        <div className="glass rounded-xl p-1 inline-flex">
          {['grid', 'multi', 'list'].map((mode) => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              className={clsx(
                'px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300',
                viewMode === mode
                  ? 'text-white shadow-lg'
                  : 'text-slate-500 hover:text-slate-800'
              )}
              style={viewMode === mode ? { background: 'var(--gradient-primary)' } : undefined}
            >
              {mode === 'grid' ? 'Grid View' : mode === 'multi' ? 'Multi View' : 'List View'}
            </button>
          ))}
        </div>
        {viewMode === 'multi' && (
          <div className="glass rounded-xl p-1 inline-flex">
            {['2x2', '3x3'].map((layout) => (
              <button
                key={layout}
                onClick={() => setGridLayout(layout)}
                className={clsx(
                  'px-3 py-2 rounded-lg text-xs font-bold transition-all',
                  gridLayout === layout
                    ? 'bg-slate-700 text-white'
                    : 'text-slate-500 hover:text-slate-800'
                )}
              >
                {layout}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Camera Grid/List */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="glass-card p-0 overflow-hidden animate-pulse">
              <div className="aspect-video bg-slate-50" />
              <div className="p-4 space-y-3">
                <div className="h-4 bg-slate-100 rounded w-3/4" />
                <div className="h-3 bg-slate-100 rounded w-1/2" />
                <div className="flex justify-between items-center">
                  <div className="h-6 bg-slate-100 rounded w-16" />
                  <div className="flex gap-2">
                    <div className="w-8 h-8 bg-slate-100 rounded" />
                    <div className="w-8 h-8 bg-slate-100 rounded" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : cameras.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <VideoCameraIcon className="w-10 h-10" />
          </div>
          <h3 className="empty-state-title">No cameras configured</h3>
          <p className="empty-state-description">Add cameras to start monitoring safety across your site.</p>
          <button
            onClick={() => setShowAddModal(true)}
            className="btn btn-primary"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            Add First Camera
          </button>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {cameras.map((camera, index) => (
            <CameraCard
              key={camera.id}
              camera={camera}
              index={index}
              onView={() => setSelectedCamera(camera)}
              onConfigure={() => navigate(`/cameras/${camera.id}/config`)}
              onEdit={() => setEditingCamera(camera)}
              onSoftDelete={() => handleSoftDelete(camera)}
              onHardDelete={() => handleHardDelete(camera)}
              onRestore={() => handleRestore(camera)}
            />
          ))}
        </div>
      ) : viewMode === 'multi' ? (
        <MultiCameraGrid
          cameras={cameras.filter(c => c.status === 'online')}
          layout={gridLayout}
          onViewCamera={(camera) => setSelectedCamera(camera)}
        />
      ) : (
        <div className="glass-card overflow-hidden animate-fade-in">
          <table className="glass-table">
            <thead>
              <tr>
                <th>Camera</th>
                <th>Location</th>
                <th>Status</th>
                <th>Last Seen</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {cameras.map((camera, index) => (
                <CameraRow
                  key={camera.id}
                  camera={camera}
                  index={index}
                  onView={() => setSelectedCamera(camera)}
                  onConfigure={() => navigate(`/cameras/${camera.id}/config`)}
                  onEdit={() => setEditingCamera(camera)}
                  onSoftDelete={() => handleSoftDelete(camera)}
                  onHardDelete={() => handleHardDelete(camera)}
                  onRestore={() => handleRestore(camera)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Camera Detail Modal */}
      {selectedCamera && (
        <CameraDetailModal
          camera={selectedCamera}
          onClose={() => setSelectedCamera(null)}
        />
      )}

      {/* Edit Camera Modal */}
      {editingCamera && (
        <EditCameraModal
          camera={editingCamera}
          onClose={() => setEditingCamera(null)}
          onSuccess={() => {
            setEditingCamera(null);
            queryClient.invalidateQueries({ queryKey: ['cameras'] });
            refetch();
          }}
        />
      )}

      {/* Add Camera Modal */}
      {showAddModal && (
        <AddCameraModal
          siteId={selectedSite?.id}
          onClose={() => setShowAddModal(false)}
          onSuccess={handleCameraAdded}
        />
      )}

      {/* ONVIF Discovery Modal */}
      <DiscoverCamerasModal
        isOpen={showDiscoverModal}
        onClose={() => setShowDiscoverModal(false)}
        siteId={selectedSite?.id}
        onCameraAdded={(cameraData) => {
          setShowDiscoverModal(false);
          setShowAddModal(true);
        }}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        title={deleteTarget?.title || 'Delete Camera'}
        message={deleteTarget?.message || ''}
        confirmLabel={deleteTarget?.confirmLabel || 'Delete'}
        variant={deleteTarget?.variant || 'danger'}
      />
    </div>
  );
}

function StatusCard({ title, count, total, color, icon: Icon, index }) {
  const colorConfig = {
    green: {
      gradient: 'from-green-500 to-emerald-500',
      glow: '',
      text: 'text-green-400',
      bg: 'bg-green-500/20',
      border: 'border-green-500/30',
    },
    gray: {
      gradient: 'from-gray-400 to-slate-500',
      glow: '',
      text: 'text-slate-500',
      bg: 'bg-slate-100',
      border: 'border-slate-200',
    },
    red: {
      gradient: 'from-red-500 to-rose-500',
      glow: '',
      text: 'text-red-400',
      bg: 'bg-red-500/20',
      border: 'border-red-500/30',
    },
  };

  const config = colorConfig[color];

  return (
    <div
      className={clsx(
        'glass-card p-5 transition-all duration-300 hover:scale-[1.02] animate-slide-up',
        count > 0 && color === 'red' && 'border-red-500/50'
      )}
      style={{ animationDelay: `${index * 0.1}s` }}
    >
      <div className="flex items-center gap-4">
        <div className={clsx(
          'w-14 h-14 rounded-xl flex items-center justify-center',
          config.bg,
          config.border,
          'border',
          count > 0 && config.glow
        )}>
          <Icon className={clsx('w-7 h-7', config.text)} />
        </div>
        <div>
          <p className="text-3xl font-bold text-slate-800">{count}</p>
          <p className="text-sm text-slate-500">{title}</p>
        </div>
        {total > 0 && (
          <div className="ml-auto">
            <span className="text-sm text-slate-400">
              {Math.round((count / total) * 100)}%
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function CameraCard({ camera, index, onView, onConfigure, onEdit, onSoftDelete, onHardDelete, onRestore }) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { connect, connected, subscribeCamera, unsubscribeCamera, activeStreams, requestSnapshot } = useLiveStreamStore();

  const statusConfig = {
    online: {
      dot: 'bg-green-500',
      glow: 'shadow-[0_0_10px_rgba(34,197,94,0.5)]',
      badge: 'badge-success',
      pulse: true,
    },
    offline: {
      dot: 'bg-slate-400',
      glow: '',
      badge: 'bg-slate-100 text-slate-500 border border-slate-200',
      pulse: false,
    },
    error: {
      dot: 'bg-red-500',
      glow: 'shadow-[0_0_10px_rgba(239,68,68,0.5)]',
      badge: 'badge-critical',
      pulse: true,
    },
  };

  const config = statusConfig[camera.status] || statusConfig.offline;

  // Use stored reference image or live snapshot
  const streamState = activeStreams[camera.id];
  const liveSnapshot = streamState?.snapshot || streamState?.frame;
  const referenceImage = liveSnapshot || camera.detectionConfig?.referenceImage;
  const hasImage = !!referenceImage;

  // Handle refresh thumbnail
  const handleRefresh = async (e) => {
    e.stopPropagation();
    if (camera.aiDevice?.status !== 'online') {
      toast.error('AI device is offline');
      return;
    }

    setIsRefreshing(true);

    // Connect if not connected
    if (!connected) {
      connect();
    }

    // Subscribe and request snapshot
    subscribeCamera(camera.id, { quality: 'medium', fps: 1 });

    // Wait a moment then request snapshot
    setTimeout(() => {
      requestSnapshot(camera.id, false);
    }, 500);

    // Unsubscribe after getting snapshot
    setTimeout(() => {
      unsubscribeCamera(camera.id);
      setIsRefreshing(false);
    }, 3000);
  };

  return (
    <div
      className={clsx(
        "glass-card overflow-hidden group animate-scale-in",
        !camera.isActive && 'opacity-60 border-slate-300'
      )}
      style={{ animationDelay: `${index * 0.05}s` }}
    >
      {/* Camera thumbnail */}
      <div className="relative aspect-video bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center overflow-hidden">
        {/* Grid pattern overlay (shown when no image) */}
        {!hasImage && <div className="absolute inset-0 bg-grid-pattern opacity-30" />}

        {/* Show stored reference image or live snapshot */}
        {isRefreshing ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-10 h-10 border-2 border-blue-400 border-t-white rounded-full animate-spin" />
            <span className="absolute mt-16 text-xs text-white/70">Refreshing...</span>
          </div>
        ) : hasImage ? (
          <>
            {!imageLoaded && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-slate-400 border-t-white rounded-full animate-spin" />
              </div>
            )}
            <img
              src={referenceImage}
              alt={camera.name}
              className={clsx(
                'w-full h-full object-cover transition-opacity duration-300',
                imageLoaded ? 'opacity-100' : 'opacity-0'
              )}
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageLoaded(true)} // Still mark as loaded to hide spinner
            />
            {/* Live indicator */}
            {liveSnapshot && (
              <div className="absolute bottom-2 left-2 flex items-center gap-1 bg-green-600/90 backdrop-blur-sm text-white px-2 py-0.5 rounded text-xs font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                LIVE
              </div>
            )}
          </>
        ) : (
          // Placeholder icon when no image available
          <div className="relative z-10 w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center backdrop-blur-sm border border-slate-200">
            <VideoCameraIcon className="w-8 h-8 text-slate-400" />
          </div>
        )}

        {/* Status indicator and refresh button */}
        <div className="absolute top-3 right-3 flex items-center gap-2">
          {/* Refresh button */}
          {camera.aiDevice?.status === 'online' && (
            <button
              onClick={handleRefresh}
              className={clsx(
                'p-1.5 rounded-lg bg-black/50 backdrop-blur-sm text-white/80 hover:text-white transition-all',
                'opacity-0 group-hover:opacity-100',
                isRefreshing && 'animate-spin'
              )}
              title="Refresh thumbnail"
              disabled={isRefreshing}
            >
              <ArrowPathIcon className="w-4 h-4" />
            </button>
          )}
          {/* Status dot */}
          <span className="relative flex h-3 w-3">
            {config.pulse && (
              <span className={clsx(
                'animate-ping absolute inline-flex h-full w-full rounded-full opacity-75',
                config.dot
              )} />
            )}
            <span className={clsx(
              'relative inline-flex rounded-full h-3 w-3',
              config.dot,
              config.glow
            )} />
          </span>
        </div>

        {/* Hover overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-4">
          {(camera.aiDevice?.status === 'online' || camera.status === 'online') ? (
            <button
              onClick={onView}
              className="btn btn-glass text-sm px-4 py-2 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300"
            >
              <EyeIcon className="w-4 h-4 mr-2" />
              View Live
            </button>
          ) : (
            <div className="text-white text-sm text-center px-4 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
              <p className="font-medium">Device Offline</p>
              <p className="text-xs text-white/70 mt-1">
                {camera.aiDevice ? 'AI engine not connected' : 'No device assigned'}
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="p-4">
        <div className="flex items-center gap-2 mb-1">
          <h3 className="font-semibold text-slate-800 truncate flex-1">{camera.name}</h3>
          {!camera.isActive && (
            <span className="badge badge-neutral text-xs opacity-70">Inactive</span>
          )}
        </div>
        <p className="text-sm text-slate-500 truncate">{camera.location || 'No location set'}</p>

        <div className="flex items-center justify-between mt-4">
          <span className={clsx('badge text-xs', config.badge)}>
            {camera.status}
          </span>
          <div className="flex gap-1">
            {camera.isActive ? (
              <>
                <button
                  onClick={onView}
                  className="p-2 rounded-lg text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-all"
                  title="View Live"
                >
                  <EyeIcon className="w-5 h-5" />
                </button>
                <button
                  onClick={onEdit}
                  className="p-2 rounded-lg text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-all"
                  title="Edit Camera"
                >
                  <PencilSquareIcon className="w-5 h-5" />
                </button>
                <button
                  onClick={onConfigure}
                  className="p-2 rounded-lg text-slate-500 hover:text-blue-600 hover:bg-blue-100 transition-all"
                  title="Configure Analytics"
                >
                  <Cog6ToothIcon className="w-5 h-5" />
                </button>
                <button
                  onClick={onSoftDelete}
                  className="p-2 rounded-lg text-slate-500 hover:text-orange-600 hover:bg-orange-100 transition-all"
                  title="Deactivate Camera"
                >
                  <StopIcon className="w-5 h-5" />
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={onRestore}
                  className="p-2 rounded-lg text-slate-500 hover:text-emerald-600 hover:bg-emerald-100 transition-all"
                  title="Restore Camera"
                >
                  <CheckIcon className="w-5 h-5" />
                </button>
                <button
                  onClick={onHardDelete}
                  className="p-2 rounded-lg text-slate-500 hover:text-red-600 hover:bg-red-100 transition-all"
                  title="Delete Camera"
                >
                  <TrashIcon className="w-5 h-5" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function CameraRow({ camera, index, onView, onConfigure, onEdit, onSoftDelete, onHardDelete, onRestore }) {
  const statusConfig = {
    online: 'badge-success',
    offline: 'bg-slate-100 text-slate-500 border border-slate-200',
    error: 'badge-critical',
  };

  return (
    <tr
      className={clsx("animate-fade-in", !camera.isActive && 'opacity-60')}
      style={{ animationDelay: `${index * 0.05}s` }}
    >
      <td>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center border border-slate-200">
            <VideoCameraIcon className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p className="font-medium text-slate-800">{camera.name}</p>
              {!camera.isActive && (
                <span className="badge badge-neutral text-xs opacity-70">Inactive</span>
              )}
            </div>
            <p className="text-sm text-slate-400">{camera.code}</p>
          </div>
        </div>
      </td>
      <td className="text-slate-500">{camera.location || '-'}</td>
      <td>
        <span className={clsx('badge text-xs', statusConfig[camera.status])}>
          {camera.status}
        </span>
      </td>
      <td className="text-sm text-slate-500">
        {camera.lastSeen ? formatDateTime(camera.lastSeen) : 'Never'}
      </td>
      <td className="text-right">
        <div className="flex items-center justify-end gap-1">
          <button
            onClick={onView}
            className="p-2 rounded-lg text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-all"
            title="View Live"
          >
            <EyeIcon className="w-4 h-4" />
          </button>
          {camera.isActive ? (
            <>
              <button
                onClick={onEdit}
                className="p-2 rounded-lg text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-all"
                title="Edit Camera"
              >
                <PencilSquareIcon className="w-4 h-4" />
              </button>
              <button
                onClick={onConfigure}
                className="p-2 rounded-lg text-slate-500 hover:text-blue-600 hover:bg-blue-100 transition-all"
                title="Configure Analytics"
              >
                <Cog6ToothIcon className="w-4 h-4" />
              </button>
              <button
                onClick={onSoftDelete}
                className="p-2 rounded-lg text-slate-500 hover:text-orange-600 hover:bg-orange-100 transition-all"
                title="Deactivate Camera"
              >
                <StopIcon className="w-4 h-4" />
              </button>
            </>
          ) : (
            <>
              <button
                onClick={onRestore}
                className="p-2 rounded-lg text-slate-500 hover:text-emerald-600 hover:bg-emerald-100 transition-all"
                title="Restore Camera"
              >
                <CheckIcon className="w-4 h-4" />
              </button>
              <button
                onClick={onHardDelete}
                className="p-2 rounded-lg text-slate-500 hover:text-red-600 hover:bg-red-100 transition-all"
                title="Delete Camera"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}

function CameraDetailModal({ camera, onClose }) {
  const {
    connected,
    subscribeCamera,
    unsubscribeCamera,
    requestSnapshot,
    activeStreams,
    setAnnotated,
  } = useLiveStreamStore();

  const [isStreaming, setIsStreaming] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [quality, setQuality] = useState('medium');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [detectionMode, setDetectionMode] = useState(() => {
    try { return localStorage.getItem('cam_detection_mode') === 'true'; } catch { return false; }
  });
  const videoContainerRef = useRef(null);
  const streamState = activeStreams[camera.id];

  const toggleFullscreen = useCallback(() => {
    if (!videoContainerRef.current) return;
    if (!document.fullscreenElement) {
      videoContainerRef.current.requestFullscreen().catch(() => {});
    } else {
      document.exitFullscreen().catch(() => {});
    }
  }, []);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  useEffect(() => {
    // Connect to live stream service on mount
    useLiveStreamStore.getState().connect();

    return () => {
      // Unsubscribe on unmount
      if (isStreaming) {
        unsubscribeCamera(camera.id, camera.aiDevice?.id);
      }
    };
  }, []);

  const handleStartStream = () => {
    subscribeCamera(camera.id, {
      quality,
      fps: quality === 'low' ? 5 : quality === 'high' ? 15 : 10,
      deviceId: camera.aiDevice?.id, // Pass AI Device ID for MQTT routing
      annotated: detectionMode,
    });
    setIsStreaming(true);
  };

  const handleStopStream = () => {
    unsubscribeCamera(camera.id, camera.aiDevice?.id);
    setIsStreaming(false);
  };

  const handleSnapshot = () => {
    requestSnapshot(camera.id, true, camera.aiDevice?.id);
    toast.success('Snapshot requested');
  };

  const handleToggleRecording = async () => {
    try {
      const { connectMqtt } = await import('../utils/mqttClient');
      const client = await connectMqtt();
      const action = isRecording ? 'stop' : 'start';
      const topic = `stream:request/${camera.aiDevice?.id}/record`;
      client.publish(topic, JSON.stringify({
        cameraId: camera.id,
        action,
        clientId: client.options.clientId,
        timestamp: new Date().toISOString(),
      }), { qos: 1 });
      setIsRecording(!isRecording);
      toast.success(isRecording ? 'Recording stopped' : 'Recording started');
    } catch {
      toast.error('Failed to toggle recording');
    }
  };

  const handleToggleDetection = () => {
    const next = !detectionMode;
    setDetectionMode(next);
    try { localStorage.setItem('cam_detection_mode', String(next)); } catch {}
    // Toggle annotated mode on active stream
    if (isStreaming) {
      setAnnotated(camera.id, next);
    }
  };

  const getStatusBadge = () => {
    if (!streamState) return null;
    const { status } = streamState;
    const colors = {
      connecting: 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30',
      streaming: 'badge-success',
      device_offline: 'badge-critical',
      stopped: 'bg-slate-100 text-slate-500 border border-slate-200',
      offline: 'badge-critical',
    };
    return (
      <span className={clsx('badge text-xs', colors[status] || colors.stopped)}>
        {status?.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-container" onClick={onClose}>
        <div className={clsx('modal-content', detectionMode ? 'max-w-6xl' : 'max-w-4xl')} onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="modal-header">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                <VideoCameraIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="modal-title">{camera.name}</h3>
                <p className="text-sm text-slate-500">{camera.code}</p>
              </div>
              {isStreaming && getStatusBadge()}
            </div>
            <button onClick={onClose} className="icon-btn">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          <div className="modal-body">
          {/* Live view + Detection panel layout */}
          <div className={clsx('flex gap-4', detectionMode ? 'mb-0' : 'mb-5')}>
            {/* Video area */}
            <div className={clsx('flex-1 min-w-0', detectionMode && 'flex-shrink-0')}>
              <div ref={videoContainerRef} className={clsx(
                "relative bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl flex items-center justify-center overflow-hidden border border-slate-200",
                isFullscreen ? 'w-screen h-screen rounded-none border-0' : 'aspect-video'
              )}>
                {/* Grid pattern */}
                <div className="absolute inset-0 bg-grid-pattern opacity-30" />

                {streamState?.frame ? (
                  <img
                    src={streamState.frame}
                    alt="Live feed"
                    className="w-full h-full object-contain relative z-10"
                  />
                ) : streamState?.snapshot ? (
                  <img
                    src={streamState.snapshot}
                    alt="Snapshot"
                    className="w-full h-full object-contain relative z-10"
                  />
                ) : (
                  <div className="text-center text-slate-500 relative z-10">
                    <div className="w-20 h-20 mx-auto mb-4 rounded-2xl bg-slate-50 flex items-center justify-center backdrop-blur-sm border border-slate-200">
                      <VideoCameraIcon className="w-10 h-10 text-slate-400" />
                    </div>
                    <p className="font-medium">Click Play to start live feed</p>
                    <p className="text-sm text-slate-400 mt-1">
                      {(camera.aiDevice?.status === 'online' || camera.status === 'online')
                        ? 'Device online - Ready to stream'
                        : camera.aiDevice
                          ? 'Device offline - AI engine not connected'
                          : 'No device assigned to this camera'}
                    </p>
                  </div>
                )}

                {/* Fullscreen toggle */}
                <button
                  onClick={toggleFullscreen}
                  className="absolute top-3 right-3 z-20 bg-black/50 text-white p-2 rounded-lg hover:bg-black/70 transition-colors backdrop-blur-sm"
                  aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                >
                  {isFullscreen ? (
                    <ArrowsPointingInIcon className="w-5 h-5" />
                  ) : (
                    <ArrowsPointingOutIcon className="w-5 h-5" />
                  )}
                </button>

                {/* Recording indicator */}
                {isRecording && (
                  <div className="absolute top-3 right-14 z-20 flex items-center gap-2 bg-red-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold">
                    <span className="w-2 h-2 rounded-full bg-white animate-pulse" aria-hidden="true" />
                    REC
                  </div>
                )}

                {/* Stream overlay info */}
                {streamState?.status === 'streaming' && (
                  <div className="absolute top-3 left-3 flex items-center gap-2 z-20">
                    <span className="flex items-center gap-2 bg-red-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
                      </span>
                      LIVE
                    </span>
                    {streamState.metadata && (
                      <span className="bg-black/50 text-white px-3 py-1.5 rounded-lg text-xs backdrop-blur-sm">
                        {streamState.metadata.width}x{streamState.metadata.height}
                      </span>
                    )}

                    {/* Detection mode indicator */}
                    {detectionMode && (
                      <span className="flex items-center gap-1.5 bg-purple-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold">
                        <CpuChipIcon className="w-3.5 h-3.5" />
                        AI
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Detection panel sidebar */}
            {detectionMode && (
              <div className="w-72 flex-shrink-0 rounded-xl border border-slate-200 overflow-hidden">
                <DetectionPanel detections={streamState?.detections} />
              </div>
            )}
          </div>

          {/* Detection event feed */}
          {detectionMode && (
            <div className="mb-5 mt-3 rounded-xl border border-slate-200 overflow-hidden h-48">
              <DetectionEventFeed detections={streamState?.detections} />
            </div>
          )}

          {!detectionMode && <div className="mb-5" />}

          {/* Stream controls */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-5 p-4 glass rounded-xl">
            <div className="flex items-center gap-3">
              {!isStreaming ? (
                <button
                  onClick={handleStartStream}
                  className="btn btn-primary flex items-center gap-2"
                  disabled={!(camera.aiDevice?.status === 'online' || camera.status === 'online')}
                  title={(camera.aiDevice?.status === 'online' || camera.status === 'online')
                    ? 'Start live video stream'
                    : 'Camera device is offline'}
                >
                  <PlayIcon className="w-5 h-5" />
                  Start Live Feed
                </button>
              ) : (
                <button
                  onClick={handleStopStream}
                  className="btn btn-secondary flex items-center gap-2"
                >
                  <StopIcon className="w-5 h-5" />
                  Stop
                </button>
              )}

              <button
                onClick={handleSnapshot}
                className="btn btn-secondary flex items-center gap-2"
                disabled={!(camera.aiDevice?.status === 'online' || camera.status === 'online')}
                title={(camera.aiDevice?.status === 'online' || camera.status === 'online')
                  ? 'Capture snapshot'
                  : 'Camera device is offline'}
              >
                <CameraIcon className="w-5 h-5" />
                Snapshot
              </button>

              <button
                onClick={handleToggleRecording}
                className={clsx(
                  'btn flex items-center gap-2',
                  isRecording ? 'bg-red-600 text-white hover:bg-red-700' : 'btn-secondary'
                )}
                disabled={!isStreaming}
                title={isRecording ? 'Stop recording' : 'Start recording'}
                aria-label={isRecording ? 'Stop recording' : 'Start recording'}
              >
                <span className={clsx('w-3 h-3 rounded-full', isRecording ? 'bg-white animate-pulse' : 'bg-red-500')} aria-hidden="true" />
                {isRecording ? 'Stop Rec' : 'Record'}
              </button>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={handleToggleDetection}
                className={clsx(
                  'btn flex items-center gap-2 text-sm',
                  detectionMode
                    ? 'bg-purple-600 text-white hover:bg-purple-700'
                    : 'btn-secondary'
                )}
                disabled={!isStreaming}
                title={detectionMode ? 'Turn off AI detection overlay' : 'Show AI detection bounding boxes and data'}
              >
                <CpuChipIcon className="w-4 h-4" />
                AI Detection
              </button>

              <label className="text-sm text-slate-500">Quality:</label>
              <select
                value={quality}
                onChange={(e) => setQuality(e.target.value)}
                className="input py-2 px-3 text-sm min-w-[120px]"
                disabled={isStreaming}
              >
                <option value="low">Low (5 FPS)</option>
                <option value="medium">Medium (10 FPS)</option>
                <option value="high">High (15 FPS)</option>
              </select>
            </div>
          </div>

          {/* Camera details grid */}
          <div className="grid grid-cols-2 gap-4">
            <div className="glass rounded-xl p-4">
              <p className="text-sm text-slate-500 mb-1">Status</p>
              <p className="font-medium text-slate-800">{camera.status}</p>
            </div>
            <div className="glass rounded-xl p-4">
              <p className="text-sm text-slate-500 mb-1">Location</p>
              <p className="font-medium text-slate-800">{camera.location || '-'}</p>
            </div>
            <div className="glass rounded-xl p-4">
              <p className="text-sm text-slate-500 mb-1">RTSP URL</p>
              <p className="font-medium text-slate-800 text-sm truncate">{camera.rtspUrl || '-'}</p>
            </div>
            <div className="glass rounded-xl p-4">
              <p className="text-sm text-slate-500 mb-1">AI Device</p>
              <div className="flex items-center gap-2">
                <p className="font-medium text-slate-800">
                  {camera.aiDevice?.name || 'Not assigned'}
                </p>
                {camera.aiDevice && (
                  <span className={clsx(
                    'badge text-xs',
                    camera.aiDevice.status === 'online' ? 'badge-success' : 'bg-slate-100 text-slate-500 border border-slate-200'
                  )}>
                    {camera.aiDevice.status}
                  </span>
                )}
              </div>
            </div>
          </div>
          </div>

          {/* Footer */}
          <div className="modal-footer">
            <button onClick={onClose} className="btn btn-secondary">Close</button>
            <button className="btn btn-primary">Edit Camera</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AddCameraModal({ siteId, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    rtspUrl: '',
    rtspUsername: '',
    rtspPassword: '',
    location: '',
    direction: '',
    coverage: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.code || !formData.rtspUrl) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!siteId) {
      toast.error('Please select a site first');
      return;
    }

    setIsSubmitting(true);

    try {
      await camerasAPI.createCamera({
        ...formData,
        siteId,
      });
      toast.success('Camera added successfully');
      onSuccess();
    } catch (error) {
      console.error('Failed to add camera:', error);
      toast.error(error.response?.data?.error || 'Failed to add camera');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-container" onClick={onClose}>
        <div className="modal-content max-w-lg" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="modal-header">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                <PlusIcon className="w-5 h-5 text-white" />
              </div>
              <h3 className="modal-title">Add New Camera</h3>
            </div>
            <button onClick={onClose} className="icon-btn">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-2">
              Camera Name <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g., Main Entrance Camera"
              className="input"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-600 mb-2">
              Camera Code <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              name="code"
              value={formData.code}
              onChange={handleChange}
              placeholder="e.g., CAM-001"
              className="input"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-600 mb-2">
              RTSP URL <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              name="rtspUrl"
              value={formData.rtspUrl}
              onChange={handleChange}
              placeholder="rtsp://192.168.1.100:554/stream"
              className="input"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-2">
                RTSP Username
              </label>
              <input
                type="text"
                name="rtspUsername"
                value={formData.rtspUsername}
                onChange={handleChange}
                placeholder="admin"
                className="input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-2">
                RTSP Password
              </label>
              <input
                type="password"
                name="rtspPassword"
                value={formData.rtspPassword}
                onChange={handleChange}
                placeholder="password"
                className="input"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-600 mb-2">
              Location
            </label>
            <input
              type="text"
              name="location"
              value={formData.location}
              onChange={handleChange}
              placeholder="e.g., Building A - Ground Floor"
              className="input"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-2">
                Direction
              </label>
              <select
                name="direction"
                value={formData.direction}
                onChange={handleChange}
                className="input"
              >
                <option value="">Select direction</option>
                <option value="north">North</option>
                <option value="south">South</option>
                <option value="east">East</option>
                <option value="west">West</option>
                <option value="northeast">Northeast</option>
                <option value="northwest">Northwest</option>
                <option value="southeast">Southeast</option>
                <option value="southwest">Southwest</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-2">
                Coverage Area
              </label>
              <input
                type="text"
                name="coverage"
                value={formData.coverage}
                onChange={handleChange}
                placeholder="e.g., Entry zone"
                className="input"
              />
            </div>
          </div>
            </div>

            {/* Footer */}
            <div className="modal-footer">
              <button
                type="button"
                onClick={onClose}
                className="btn btn-secondary"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary flex items-center gap-2"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white"></div>
                    Adding...
                  </>
                ) : (
                  <>
                    <PlusIcon className="w-5 h-5" />
                    Add Camera
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function MultiCameraGrid({ cameras, layout, onViewCamera }) {
  const { subscribeCamera, unsubscribeCamera, activeStreams, connected } = useLiveStreamStore();
  const cols = layout === '3x3' ? 3 : 2;
  const totalSlots = cols * cols;
  const displayCameras = cameras.slice(0, totalSlots);

  useEffect(() => {
    if (!connected) return;
    displayCameras.forEach((cam) => {
      if (!activeStreams[cam.id]?.subscribed) {
        subscribeCamera(cam.id, { quality: 'low' });
      }
    });
    return () => {
      displayCameras.forEach((cam) => {
        if (activeStreams[cam.id]?.subscribed) {
          unsubscribeCamera(cam.id, cam.aiDeviceId);
        }
      });
    };
  }, [connected, displayCameras.map(c => c.id).join(',')]);

  return (
    <div
      className="animate-fade-in"
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${cols}, 1fr)`,
        gap: '0.75rem',
      }}
    >
      {Array.from({ length: totalSlots }).map((_, idx) => {
        const cam = displayCameras[idx];
        if (!cam) {
          return (
            <div key={`empty-${idx}`} className="glass-card aspect-video flex items-center justify-center">
              <VideoCameraIcon className="w-10 h-10 text-slate-300" />
            </div>
          );
        }
        const stream = activeStreams[cam.id];
        return (
          <div
            key={cam.id}
            className="glass-card p-0 overflow-hidden cursor-pointer group relative"
            onClick={() => onViewCamera(cam)}
          >
            <div className="aspect-video bg-slate-900 relative">
              {stream?.frame ? (
                <img
                  src={stream.frame}
                  alt={cam.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400 gap-2">
                  <div className="animate-spin rounded-full h-6 w-6 border-2 border-slate-600 border-t-slate-300" />
                  <span className="text-xs">Connecting...</span>
                </div>
              )}
              {/* Camera name overlay */}
              <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                <div className="flex items-center justify-between">
                  <span className="text-white text-sm font-medium truncate">{cam.name}</span>
                  {stream?.subscribed && (
                    <span className="flex items-center gap-1 text-xs text-red-400 font-bold">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                      LIVE
                    </span>
                  )}
                </div>
              </div>
              {/* Hover overlay */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                <span className="text-white text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                  View Full
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function EditCameraModal({ camera, onClose, onSuccess }) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    name: camera?.name || '',
    code: camera?.code || '',
    rtspUrl: camera?.rtspUrl || '',
    rtspUsername: camera?.rtspUsername || '',
    rtspPassword: camera?.rtspPassword || '',
    location: camera?.location || '',
    direction: camera?.direction || '',
    coverage: camera?.coverage || '',
    mountHeight: camera?.mountHeight || null,
    fieldOfView: camera?.fieldOfView || null,
    aiDeviceId: camera?.aiDeviceId || null,
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Camera name is required';
    if (!formData.code.trim()) newErrors.code = 'Camera code is required';
    if (!formData.rtspUrl.trim()) newErrors.rtspUrl = 'RTSP URL is required';
    if (formData.mountHeight && isNaN(parseFloat(formData.mountHeight))) {
      newErrors.mountHeight = 'Must be a valid number';
    }
    if (formData.fieldOfView && isNaN(parseFloat(formData.fieldOfView))) {
      newErrors.fieldOfView = 'Must be a valid number';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      // Prepare data: convert empty strings to null for numeric fields
      const submitData = {
        ...formData,
        mountHeight: formData.mountHeight === '' ? null : formData.mountHeight,
        fieldOfView: formData.fieldOfView === '' ? null : formData.fieldOfView,
        aiDeviceId: formData.aiDeviceId === '' ? null : formData.aiDeviceId,
        rtspUsername: formData.rtspUsername === '' ? null : formData.rtspUsername,
        rtspPassword: formData.rtspPassword === '' ? null : formData.rtspPassword,
        location: formData.location === '' ? null : formData.location,
        direction: formData.direction === '' ? null : formData.direction,
        coverage: formData.coverage === '' ? null : formData.coverage,
      };

      await camerasAPI.updateCamera(camera.id, submitData);
      toast.success('Camera updated successfully');
      onSuccess();
      onClose();
    } catch (error) {
      const message = error.response?.data?.message || error.response?.data?.error || 'Failed to update camera';
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-container" onClick={onClose}>
        <div className="modal-content max-w-2xl" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="modal-header">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                <CheckIcon className="w-5 h-5 text-white" />
              </div>
              <h3 className="modal-title">Edit Camera</h3>
            </div>
            <button onClick={onClose} className="icon-btn">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-2">
                  Camera Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="e.g., Entrance Front Gate"
                  className={`input w-full ${errors.name ? 'input-error' : ''}`}
                />
                {errors.name && <span className="text-xs text-red-500 mt-1">{errors.name}</span>}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-600 mb-2">
                  Camera Code <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="code"
                  value={formData.code}
                  onChange={handleChange}
                  placeholder="e.g., CAM-001"
                  className={`input w-full ${errors.code ? 'input-error' : ''}`}
                />
                {errors.code && <span className="text-xs text-red-500 mt-1">{errors.code}</span>}
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-600 mb-2">
                  RTSP URL <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="rtspUrl"
                  value={formData.rtspUrl}
                  onChange={handleChange}
                  placeholder="rtsp://192.168.1.100:554/stream"
                  className={`input w-full ${errors.rtspUrl ? 'input-error' : ''}`}
                />
                {errors.rtspUrl && <span className="text-xs text-red-500 mt-1">{errors.rtspUrl}</span>}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-600 mb-2">
                  RTSP Username
                </label>
                <input
                  type="text"
                  name="rtspUsername"
                  value={formData.rtspUsername}
                  onChange={handleChange}
                  placeholder="admin"
                  className="input w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-600 mb-2">
                  RTSP Password
                </label>
                <input
                  type="password"
                  name="rtspPassword"
                  value={formData.rtspPassword}
                  onChange={handleChange}
                  placeholder="password"
                  className="input w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-600 mb-2">
                  Location
                </label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  placeholder="e.g., Main Entrance"
                  className="input w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-600 mb-2">
                  Direction
                </label>
                <input
                  type="text"
                  name="direction"
                  value={formData.direction}
                  onChange={handleChange}
                  placeholder="e.g., North"
                  className="input w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-600 mb-2">
                  Coverage Area
                </label>
                <input
                  type="text"
                  name="coverage"
                  value={formData.coverage}
                  onChange={handleChange}
                  placeholder="e.g., Lobby"
                  className="input w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-600 mb-2">
                  Mount Height (m)
                </label>
                <input
                  type="number"
                  name="mountHeight"
                  value={formData.mountHeight}
                  onChange={handleChange}
                  placeholder="e.g., 2.5"
                  step="0.1"
                  className={`input w-full ${errors.mountHeight ? 'input-error' : ''}`}
                />
                {errors.mountHeight && (
                  <span className="text-xs text-red-500 mt-1">{errors.mountHeight}</span>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-600 mb-2">
                  Field of View (°)
                </label>
                <input
                  type="number"
                  name="fieldOfView"
                  value={formData.fieldOfView}
                  onChange={handleChange}
                  placeholder="e.g., 90"
                  step="0.1"
                  className={`input w-full ${errors.fieldOfView ? 'input-error' : ''}`}
                />
                {errors.fieldOfView && (
                  <span className="text-xs text-red-500 mt-1">{errors.fieldOfView}</span>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-600 mb-2">
                  AI Device ID
                </label>
                <input
                  type="text"
                  name="aiDeviceId"
                  value={formData.aiDeviceId}
                  onChange={handleChange}
                  placeholder="Optional"
                  className="input w-full"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="modal-footer">
              <button
                type="button"
                onClick={onClose}
                className="btn btn-secondary"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary flex items-center gap-2"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckIcon className="w-5 h-5" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

